import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from './utils';

interface CommonFieldProps {
  label: string;
  /** Optional helper text shown below the field. */
  hint?: ReactNode;
  /** Optional error message; when present overrides the hint and adds rose styling. */
  error?: ReactNode;
  /** Trailing icon / button rendered inside the field's right edge (e.g. show password). */
  trailing?: ReactNode;
  className?: string;
}

export type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'placeholder'> &
  CommonFieldProps;

/**
 * Floating-label text input — wraps the global `.field` styles.
 * Use `<Field>` for single-line, `<TextField>` interchangeably, or `<TextArea>` for multi-line.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, trailing, className, id, type = 'text', ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `field-${reactId}`;
  return (
    <div className={cn('w-full', className)}>
      <div className={cn('ui-field', error && '!border-[color:var(--color-rose)]')}>
        <input
          ref={ref}
          id={fieldId}
          type={type}
          placeholder=" "
          {...rest}
          className={cn('field-input focus-ring', trailing && 'pr-12')}
        />
        <label htmlFor={fieldId} className="field-label">
          {label}
        </label>
        {trailing && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 inline-flex items-center">
            {trailing}
          </span>
        )}
      </div>
      {(hint || error) && (
        <p
          className={cn(
            'mt-1.5 text-[11.5px] leading-snug',
            error ? 'text-[color:var(--color-rose)]' : 'text-ink-500',
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});

export type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder'> &
  CommonFieldProps;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, hint, error, className, id, ...rest },
  ref,
) {
  const reactId = useId();
  const fieldId = id ?? `field-${reactId}`;
  return (
    <div className={cn('w-full', className)}>
      <div className={cn('ui-field !rounded-xl', error && '!border-[color:var(--color-rose)]')}>
        <textarea
          ref={ref}
          id={fieldId}
          placeholder=" "
          {...rest}
          className={cn(
            'field-input focus-ring resize-none w-full bg-transparent outline-none px-3.5 pt-6 pb-3',
          )}
        />
        <label htmlFor={fieldId} className="field-label">
          {label}
        </label>
      </div>
      {(hint || error) && (
        <p
          className={cn(
            'mt-1.5 text-[11.5px] leading-snug',
            error ? 'text-[color:var(--color-rose)]' : 'text-ink-500',
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});
